import _ from 'lodash';
import React from 'react';

import {
  Add,
  DeleteForever
} from '@mui/icons-material';
import {
  Button,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography
} from '@mui/material';
import {
  fields,
  Form,
  formUtil
} from 'iron-forge-react-modules';
import {
  BaseWidget,
  selectFields
} from 'modules/layer01';
import {
  useFieldArray,
  useForm
} from 'react-hook-form';

import { appFormUtil } from 'modules/layer00';
import * as dataService from './dataService';
import * as types from './types';



const ControlledTextField = fields.TextField.genControllerComponent<types.IFormState>();
const { genController } = selectFields.ThirdPartyAccountSelectField.genComponentAndControllerGenerator();
const ControlledThirdPartyAccountSelectField = genController<types.IFormState>();



export interface IProps {
  storeId: string;
}

export const FC: React.FC<IProps> = props => {
  const queryTuple = dataService.useQuery(props.storeId);
  const [saveShippingServices, saveShippingServicesTuple] = dataService.useSaveShippingServices(props.storeId);

  const form = useForm<types.IFormState>({
    defaultValues: {
      shippingServices: []
    }
  });
  const fieldArray = useFieldArray({
    control: form.control,
    name: 'shippingServices'
  });
  formUtil.useSetFormFields(queryTuple, form.setValue, ['shippingServices']);



  const ControlledShipItemField = React.useMemo(
    () => {
      if (queryTuple.data.subsidiaryId) {
        const {
          genController
        } = selectFields.ShipItemField.genComponentAndControllerGenerator(queryTuple.data.subsidiaryId);

        return genController<types.IFormState>();
      }

      return null;

    },
    [queryTuple.data.subsidiaryId]
  );


  const processSubmit = React.useCallback(
    (formState: types.IFormState) => {
      const trimmedFormState = appFormUtil.trimFormStateValues(formState);
      const uniqueRows = _.uniqBy(trimmedFormState.shippingServices, s => s.requestedShippingService);
      if (uniqueRows.length !== trimmedFormState.shippingServices.length) {
        alert("The mapped strings must be unique for this store.");
        return;
      }
      saveShippingServices(trimmedFormState);
    },
    [saveShippingServices]
  );

  const handleClickNewShippingService = React.useCallback(
    () => {
      fieldArray.append({
        id: '',
        requestedShippingService: '',
        shipItem: null,
        thirdPartyAccount: null
      });
    },
    [fieldArray]
  );

  const handleClickDelete = React.useCallback(
    (index: number) => {
      fieldArray.remove(index);
    },
    [fieldArray]
  );

  const handleClickAddUnmappedString = React.useCallback(
    (requestedShippingService: string) => {
      fieldArray.append({
        id: '',
        requestedShippingService,
        shipItem: null,
        thirdPartyAccount: null
      });
    },
    [fieldArray]
  );



  const loading = queryTuple.loading || saveShippingServicesTuple.loading;


  const unmappedStrings = React.useMemo(
    () => _.filter(queryTuple.data.unmappedStrings, str => {
      const found =_.find(fieldArray.fields, field => field.requestedShippingService === str);
      return _.isNil(found);
    }),
    [queryTuple.data.unmappedStrings, fieldArray.fields]
  );

  return (
    <Form.FC
      disableDefaultOverflow
      onSubmit={form.handleSubmit(processSubmit)}>
      <BaseWidget.FC
        title="Shipping Service"
        elAction={(
          <Button
            type="submit"
            variant="contained">
            Save
          </Button>
        )}>
        <Grid container>
          <Grid
            container
            item
            xs={12}
            md={8}
            rowSpacing={2}
            columnSpacing={1}>
            {fieldArray.fields.map((field, index) => (
              <React.Fragment key={field.id}>
                <Grid
                  item
                  xs={4}
                  md={4}>
                  <ControlledTextField
                    isDisabled={loading}
                    fieldKey={`shippingServices.${index}.requestedShippingService`}
                    formControl={form.control} />
                </Grid>

                <Grid
                  item
                  xs={3}
                  md={4}>
                  {ControlledShipItemField && (
                    <ControlledShipItemField
                      isDisabled={loading}
                      isRequired
                      fieldKey={`shippingServices.${index}.shipItem`}
                      formControl={form.control} />
                  )}
                </Grid>

                <Grid
                  item
                  xs={4}
                  md={3}>
                  <ControlledThirdPartyAccountSelectField
                    isDisabled={loading}
                    fieldKey={`shippingServices.${index}.thirdPartyAccount`}
                    formControl={form.control}
                    label="Third Party Account" />
                </Grid>

                <Grid
                  item
                  xs={1}>
                  <IconButton
                    disabled={loading}
                    onClick={() => handleClickDelete(index)}>
                    <DeleteForever />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}

            <Grid
              item
              xs={12}>
              <Button
                disabled={loading}
                startIcon={(<Add />)}
                onClick={handleClickNewShippingService}>
                New Shipping Service
              </Button>
            </Grid>
          </Grid>

          <Grid
            item
            xs={12}
            md={4}>
            <Typography>Suggested mappings for this store:</Typography>

            <List>
              {_.map(unmappedStrings, str => (
                <ListItem key={str}>
                  <ListItemButton
                    onClick={() => handleClickAddUnmappedString(str)}>
                    <ListItemIcon color="warning">
                      <Add />
                    </ListItemIcon>

                    <ListItemText
                      primary={str}
                      color="warning" />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </BaseWidget.FC>
    </Form.FC>
  );
};
